import { api } from "@repo/convex/api";
import type { Doc, Id } from "@repo/convex/dataModel";
import { buildProjectClarificationThreadSearchParams } from "@repo/shared";
import { useConvex } from "convex/react";
import { Bell, CheckCheck } from "lucide-react";
import { useCallback, useState } from "react";
import { toast } from "sonner";
import { Button, buttonVariants } from "@/components/ui/button";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { UnreadDot } from "@/components/ui/unread-dot";
import { useOrganization } from "@/contexts/organization-context";
import { useTabsActions } from "@/contexts/tabs-context";
import { cn } from "@/lib/utils";
import { makeProjectTabId } from "@/routing/core/locations";
import {
  useMarkAllNotificationsAsRead,
  useMarkNotificationAsRead,
  useNotificationProjectLookups,
  useNotifications,
  useRemoveNotification,
  useUnreadNotificationCount,
} from "../hooks";
import { NotificationList } from "./notification-list";

export function NotificationBell() {
  const [open, setOpen] = useState(false);
  const convex = useConvex();
  const { organizationId } = useOrganization();
  const { openProjectTab, navigateTab } = useTabsActions();

  // Data
  const unreadCount = useUnreadNotificationCount(organizationId);
  const notifications = useNotifications(organizationId, { enabled: open, limit: 100 });
  const projectLookupById = useNotificationProjectLookups(organizationId, notifications, open);

  // Mutations
  const markAsRead = useMarkNotificationAsRead();
  const markAllAsRead = useMarkAllNotificationsAsRead();
  const removeNotification = useRemoveNotification();

  const getProjectName = useCallback(
    (projectId: Id<"projects">) => projectLookupById?.get(projectId)?.name,
    [projectLookupById]
  );

  const handleMarkAllAsRead = async () => {
    if (!organizationId) return;
    await markAllAsRead({ orgId: organizationId });
  };

  const handleRemove = async (notificationId: Doc<"notifications">["_id"]) => {
    if (!organizationId) return;
    await removeNotification({ orgId: organizationId, notificationId });
  };

  const handleNavigate = async (notification: Doc<"notifications">) => {
    if (!organizationId) return;

    // Mark as read
    if (!notification.readAt) {
      await markAsRead({ orgId: organizationId, notificationId: notification._id });
    }

    // Close popover
    setOpen(false);

    try {
      const target = await convex.query(api.notifications.resolveNavigationTarget, {
        orgId: organizationId,
        notificationId: notification._id,
      });

      if (target.action === "navigate") {
        const hasClarificationSearch =
          typeof target.search?.crs === "string" && target.search.crs.length > 0;
        const targetSearch = hasClarificationSearch
          ? target.search
          : notification.target.kind === "clarificationRequest"
            ? buildProjectClarificationThreadSearchParams(
                notification.target.clarificationRequestId
              )
            : target.search;
        const tabId = makeProjectTabId(target.projectSlug);
        const projectTab = { id: target.projectSlug, name: target.projectName };
        openProjectTab(projectTab);
        navigateTab(tabId, target.path, targetSearch);
        return;
      }

      if (target.action === "missing") {
        toast.error(target.message);
      }
    } catch (error) {
      toast.error(
        error instanceof Error
          ? error.message
          : "Unable to open this notification because project navigation is misconfigured."
      );
    }
  };

  const hasUnread = unreadCount != null && unreadCount > 0;
  const hasProjectNotifications =
    notifications?.some((notification) => notification.projectId !== undefined) ?? false;
  const notificationListData =
    hasProjectNotifications && projectLookupById === undefined ? undefined : notifications;

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger
        aria-label={hasUnread ? `Notifications, ${unreadCount} unread` : "Notifications"}
        className={cn(
          buttonVariants({ variant: "ghost", size: "icon-sm" }),
          "relative text-muted-foreground"
        )}
      >
        <Bell className="size-4" />
        {hasUnread ? <UnreadDot className="absolute right-0.5 top-0.5" /> : null}
      </PopoverTrigger>

      <PopoverContent side="bottom" align="start" sideOffset={8} className="w-96 gap-0 p-0">
        {/* Header */}
        <div className="flex items-center justify-between border-b px-3 py-2">
          <h3 className="text-sm font-semibold">Notifications</h3>
          {unreadCount && unreadCount > 0 ? (
            <Button
              variant="ghost"
              size="sm"
              className="h-auto px-2 py-1 text-xs text-muted-foreground"
              onClick={handleMarkAllAsRead}
            >
              <CheckCheck className="mr-1 size-3" />
              Mark all read
            </Button>
          ) : null}
        </div>

        {/* Scrollable list */}
        <div className="max-h-96 overflow-y-auto p-1">
          <NotificationList
            notifications={notificationListData}
            getProjectName={getProjectName}
            onNavigate={handleNavigate}
            onRemove={handleRemove}
          />
        </div>
      </PopoverContent>
    </Popover>
  );
}
